import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import { apiClient } from '../../utils/api';

const RACIMatrixPage = () => {
  const { project } = useOutletContext();
  const [raciMatrix, setRaciMatrix] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [title, setTitle] = useState('RACI MATRIX');
  const [implementationType, setImplementationType] = useState('');
  const [clientName, setClientName] = useState('Client Name');
  const [rfpNumber, setRfpNumber] = useState('2025-08-0053');
  const [matrixDate, setMatrixDate] = useState('');
  const loadedProjectRef = useRef(null);

  // RACI role columns - memoized to prevent recreation
  const roleColumns = useMemo(() => [
    { key: 'si_pm', label: 'SLC PM', tooltip: 'SLC Project Manager' },
    { key: 'si_architect', label: 'SLC Architect', tooltip: 'SLC Solution Architect' },
    { key: 'si_functional', label: 'SLC Functional', tooltip: 'SLC Finance/Controlling Lead' },
    { key: 'si_technical', label: 'SLC Technical', tooltip: 'SLC Integration/Basis/ABAP Team' },
    { key: 'si_training', label: 'SLC Training', tooltip: 'SLC Training Lead' },
    { key: 'si_change_mgmt', label: 'SLC Change Mgmt', tooltip: 'SLC Change Management Consultant' },
    { key: 'mehvac_exec_sponsor', label: 'Customer Exec Sponsor', tooltip: 'Customer Executive Leadership' },
    { key: 'mehvac_project_sponsor', label: 'Customer Project Sponsor', tooltip: 'Customer Project Sponsor' },
    { key: 'mehvac_pm', label: 'Customer PM', tooltip: 'Customer Project Manager/Coordinator' },
    { key: 'mehvac_smes', label: 'Customer SMEs', tooltip: 'Customer Subject Matter Experts' },
    { key: 'mehvac_it', label: 'Customer IT', tooltip: 'Customer IT Infrastructure/Basis Team' },
    { key: 'end_users', label: 'End Users', tooltip: 'Customer End User Community' }
  ], []);

  // RACI options - memoized to prevent recreation
  const raciOptions = useMemo(() => ['R', 'A', 'C', 'I', 'A/R', ''], []);

  const loadRACIMatrix = useCallback(async () => {
    if (!project?.id) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiClient.get(`/projects/${project.id}/raci-matrix`);
      
      if (response.data.success) {
        setRaciMatrix(response.data.raci_matrix);
        setActivities(response.data.activities);
        setTitle(response.data.raci_matrix.title || 'RACI MATRIX');
        
        // Use details extracted from project documents or fall back to defaults
        setImplementationType(
          response.data.raci_matrix.implementation_type || 
          project.implementation_type || 
          project.project_title || 
          project.name || 
          'SAP S/4HANA Greenfield Implementation'
        );
        setClientName(
          response.data.raci_matrix.client_name || 
          project.client_name || 
          'Client Name'
        );
        setRfpNumber(
          response.data.raci_matrix.rfp_number || 
          project.rfp_number || 
          '2025-08-0053'
        );
        setMatrixDate(response.data.raci_matrix.matrix_date || new Date().toISOString().split('T')[0]);
      } else {
        setError(response.data.error || 'Failed to load RACI matrix');
      }
    } catch (error) {
      console.error('Error loading RACI matrix:', error);
      setError('Failed to load RACI matrix. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => {
    if (project?.id && loadedProjectRef.current !== project.id) {
      loadedProjectRef.current = project.id;
      loadRACIMatrix();
    }
  }, [project?.id, loadRACIMatrix]);

  const updateActivity = useCallback(async (activityId, field, value) => {
    try {
      const response = await apiClient.put(`/projects/${project.id}/raci-matrix/activities/${activityId}`, {
        [field]: value
      });
      
      if (response.data.success) {
        // Update local state
        setActivities(activities.map(activity => 
          activity.id === activityId 
            ? { ...activity, [field]: value }
            : activity
        ));
        setSuccess('Activity updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Failed to update activity');
      }
    } catch (error) {
      console.error('Error updating activity:', error);
      setError('Failed to update activity. Please try again.');
    }
  }, [project?.id, activities]);

  const saveMetadata = async () => {
    try {
      setSaving(true);
      setError('');
      
      const response = await apiClient.put(`/projects/${project.id}/raci-matrix`, {
        title: title,
        implementation_type: implementationType,
        client_name: clientName,
        rfp_number: rfpNumber,
        matrix_date: matrixDate
      });
      
      if (response.data.success) {
        setRaciMatrix(response.data.raci_matrix);
        setEditingMetadata(false);
        setSuccess('RACI matrix metadata updated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.error || 'Failed to update metadata');
      }
    } catch (error) {
      console.error('Error updating metadata:', error);
      setError('Failed to update metadata. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const downloadWordDocument = async () => {
    try {
      setError('');
      const response = await apiClient.get(`/projects/${project.id}/raci-matrix/export`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RACI_Matrix_${project.name}_${new Date().toISOString().split('T')[0]}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('RACI Matrix downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error downloading RACI matrix:', error);
      setError('Failed to download RACI matrix. Please try again.');
    }
  };

  const groupedActivities = useMemo(() => {
    const groups = {};
    activities.forEach(activity => {
      if (!groups[activity.phase_name]) {
        groups[activity.phase_name] = [];
      }
      groups[activity.phase_name].push(activity);
    });
    return groups;
  }, [activities]);

  const RACICell = memo(({ activity, field }) => (
    <TableCell sx={{ minWidth: 80, padding: '4px' }}>
      <FormControl size="small" fullWidth>
        <Select
          value={activity[field] || ''}
          onChange={(e) => updateActivity(activity.id, field, e.target.value)}
          displayEmpty
          sx={{ 
            fontSize: '0.75rem',
            '& .MuiSelect-select': {
              padding: '2px 8px',
              minHeight: 'unset'
            }
          }}
        >
          {raciOptions.map(option => (
            <MenuItem key={option} value={option} sx={{ fontSize: '0.75rem' }}>
              {option || 'None'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </TableCell>
  ));

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading RACI Matrix...</Typography>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Project not found</Alert>
      </Container>
    );
  }


  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {editingMetadata ? (
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ 
                '& .MuiInputBase-input': { 
                  fontSize: '2rem', 
                  fontWeight: 'bold' 
                }
              }}
            />
          ) : (
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Download as Word Document">
              <IconButton 
                onClick={downloadWordDocument}
                color="primary"
                sx={{ 
                  bgcolor: 'primary.light',
                  '&:hover': { bgcolor: 'primary.main', color: 'white' }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Metadata">
              <IconButton 
                onClick={() => setEditingMetadata(true)}
                color="primary"
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {editingMetadata ? (
          <TextField
            label="Implementation Type"
            value={implementationType}
            onChange={(e) => setImplementationType(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="h6" gutterBottom>
            {implementationType}
          </Typography>
        )}
        
        {editingMetadata ? (
          <TextField
            label="Client Name"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="h6" gutterBottom>
            {clientName}
          </Typography>
        )}
        
        {/* Info message about project document extraction */}
        {(project.client_name || project.implementation_type || project.rfp_number) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            📋 Project details below were automatically extracted from the uploaded project documents. You can edit them if needed.
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            {editingMetadata ? (
              <TextField
                label="Project Name"
                value={project.name}
                disabled
                fullWidth
                variant="outlined"
                size="small"
                helperText="Project name from database"
              />
            ) : (
              <Typography><strong>Project:</strong> {project.name}</Typography>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            {editingMetadata ? (
              <TextField
                label="RFP No"
                value={rfpNumber}
                onChange={(e) => setRfpNumber(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
            ) : (
              <Typography><strong>RFP No:</strong> {raciMatrix?.rfp_number}</Typography>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            {editingMetadata ? (
              <TextField
                label="Date"
                type="date"
                value={matrixDate}
                onChange={(e) => setMatrixDate(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Typography><strong>Date:</strong> {raciMatrix?.matrix_date}</Typography>
            )}
          </Grid>
        </Grid>

        {editingMetadata && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={saveMetadata}
              disabled={saving}
              sx={{ mr: 1 }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEditingMetadata(false)}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Paper>

      {/* RACI Key */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            RACI KEY
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip label="R" color="primary" size="small" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Responsible</strong> - Person/team who does the work to complete the task
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip label="A" color="secondary" size="small" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Accountable</strong> - Person who is ultimately answerable and has yes/no/veto authority
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip label="C" color="info" size="small" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Consulted</strong> - People who provide input and with whom there is two-way communication
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip label="I" color="warning" size="small" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  <strong>Informed</strong> - People who are kept up-to-date on progress
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* RACI Matrix by Phase */}
      {Object.entries(groupedActivities).map(([phaseName, phaseActivities]) => (
        <Accordion key={phaseName} defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {phaseName}
            </Typography>
            <Chip 
              label={`${phaseActivities.length} activities`} 
              size="small" 
              sx={{ ml: 2 }} 
            />
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200, position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 10 }}>
                      Activity
                    </TableCell>
                    {roleColumns.map(role => (
                      <TableCell 
                        key={role.key} 
                        sx={{ 
                          fontWeight: 'bold', 
                          minWidth: 80, 
                          textAlign: 'center',
                          fontSize: '0.75rem',
                          padding: '8px 4px'
                        }}
                      >
                        <Tooltip title={role.tooltip} arrow>
                          <Box>
                            {role.label}
                            <InfoIcon sx={{ fontSize: '0.7rem', ml: 0.5 }} />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phaseActivities.map((activity) => (
                    <TableRow key={activity.id} hover>
                      <TableCell 
                        sx={{ 
                          fontWeight: 'medium', 
                          position: 'sticky', 
                          left: 0, 
                          backgroundColor: 'background.paper',
                          zIndex: 9,
                          borderRight: 1,
                          borderColor: 'divider'
                        }}
                      >
                        {activity.activity_name}
                      </TableCell>
                      {roleColumns.map(role => (
                        <RACICell 
                          key={role.key} 
                          activity={activity} 
                          field={role.key} 
                        />
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Footer Information */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          CRITICAL SUCCESS FACTORS
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              SLC Responsibilities
            </Typography>
            <Typography variant="body2" component="div">
              <ol>
                <li><strong>Dedicated Team:</strong> Maintain committed, experienced resources throughout project</li>
                <li><strong>Knowledge Transfer:</strong> Proactively transfer knowledge to Customer team</li>
                <li><strong>Quality Deliverables:</strong> Deliver high-quality, complete work products on time</li>
                <li><strong>Proactive Communication:</strong> Identify and communicate risks/issues early</li>
                <li><strong>Best Practices:</strong> Apply SAP and industry best practices</li>
                <li><strong>Flexibility:</strong> Adapt to Customer's evolving needs while managing scope</li>
              </ol>
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Customer Responsibilities
            </Typography>
            <Typography variant="body2" component="div">
              <ol>
                <li><strong>Executive Sponsorship:</strong> Active executive engagement and support</li>
                <li><strong>SME Availability:</strong> Timely availability of subject matter experts per schedule</li>
                <li><strong>Timely Decisions:</strong> Make decisions promptly to avoid delays</li>
                <li><strong>Master Data Creation:</strong> Complete master data creation per templates and timeline</li>
                <li><strong>Infrastructure Readiness:</strong> Ensure AWS and SAP licenses ready on time</li>
                <li><strong>Change Management:</strong> Support organizational change and user adoption</li>
                <li><strong>Testing Participation:</strong> Active participation in UAT and testing activities</li>
              </ol>
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default RACIMatrixPage;
